// ==========================================================================
// SignalFix v2 — Uploaded-elements adapter
// --------------------------------------------------------------------------
// The v2 element dataset is stored per client_type on the uploads server
// (REACT_APP_UPLOADS_API_URL) and retrieved exactly like the other SignalFix
// files, e.g. fetchClientJSON(clientType, SIGNALFIX_ELEMENTS_PATH).
//
// The stored structure MAY change over time, so this adapter is deliberately
// tolerant: it accepts a plain array, a wrapped object ({elements|signals|
// data: [...]}), or a GeoJSON FeatureCollection, and it accepts common field
// aliases. Everything downstream consumes the normalised sign shape, so if the
// upstream structure changes only this file needs updating.
// ==========================================================================

import { normalizeElementKey } from "./signalElements";

// Relative path under the client's uploads folder (per client_type).
export const SIGNALFIX_ELEMENTS_PATH = "signal_fix/signalfix_elements.json";

const firstDefined = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");

// Pull the array of raw records out of whatever container shape we received.
const extractRecords = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.features)) return raw.features; // GeoJSON FeatureCollection
  if (Array.isArray(raw.elements)) return raw.elements;
  if (Array.isArray(raw.signals)) return raw.signals;
  if (Array.isArray(raw.data)) return raw.data;
  return [];
};

// GeoJSON coordinates are [lng, lat]; the app works in [lat, lng].
const toLatLng = (coord) =>
  Array.isArray(coord) && coord.length >= 2 ? [coord[1], coord[0]] : null;

// Derive lat/lng (+ polyline geometry) from a GeoJSON geometry object.
const fromGeoJsonGeometry = (geom) => {
  if (!geom || !geom.type) return {};
  const { type, coordinates } = geom;

  if (type === "Point") {
    const ll = toLatLng(coordinates);
    return ll ? { lat: ll[0], lng: ll[1] } : {};
  }

  const rawLine =
    type === "LineString"
      ? coordinates
      : type === "MultiLineString"
        ? coordinates?.[0]
        : type === "Polygon"
          ? coordinates?.[0]
          : null;

  if (Array.isArray(rawLine)) {
    const line = rawLine.map(toLatLng).filter(Boolean);
    if (line.length >= 2) {
      const mid = line[Math.floor(line.length / 2)];
      return { lat: mid[0], lng: mid[1], geometry: line };
    }
  }
  return {};
};

// Normalise a single record (plain object OR GeoJSON feature) to a sign.
const normalizeRecord = (record, index) => {
  if (!record || typeof record !== "object") return null;

  // GeoJSON features keep their attributes under `properties`.
  const props =
    record.properties && typeof record.properties === "object" ? record.properties : record;

  let lat = firstDefined(props.lat, props.latitude, record.lat, record.latitude);
  let lng = firstDefined(props.lng, props.lon, props.longitude, record.lng, record.lon, record.longitude);
  let geometry = firstDefined(props.geometry, props.coords, props.path, props.line);

  // A GeoJSON geometry object (Point / LineString / ...) takes precedence.
  if (record.geometry && record.geometry.type) {
    const g = fromGeoJsonGeometry(record.geometry);
    if (g.lat !== undefined) lat = g.lat;
    if (g.lng !== undefined) lng = g.lng;
    if (g.geometry) geometry = g.geometry;
  }

  if (lat === undefined || lat === null || lng === undefined || lng === null) return null;

  const type = firstDefined(props.type, props.class, props.label, props.tipo, "unknown");
  const key = normalizeElementKey(type);
  const externalId = firstDefined(props.external_id, props.detection_id, props.id, record.id, `up-${index}`);
  const isSegnaletica = props.Segnaletica === true || props.segnaletica === true;

  return {
    id: firstDefined(record.id, props.id, externalId),
    external_id: externalId,
    lat: Number(lat),
    lng: Number(lng),
    type,
    description: firstDefined(props.description, props.label, key),
    category: firstDefined(props.category, undefined),
    Segnaletica: isSegnaletica,
    image_url: firstDefined(props.image_url, props.image, props.frame_path, "") || "",
    street: firstDefined(props.street, props.address, props.indirizzo, "") || "",
    support: firstDefined(props.support, props.supporto, undefined),
    geometry: Array.isArray(geometry) && geometry.length >= 2 ? geometry : undefined,
    geometryKind: firstDefined(props.geometryKind, props.geometry_kind, undefined),
    is_sample: false,
  };
};

// Public: normalise the raw uploaded payload into an array of sign objects.
export const normalizeUploadedElements = (raw) => {
  try {
    return extractRecords(raw)
      .map(normalizeRecord)
      .filter((s) => s && !isNaN(s.lat) && !isNaN(s.lng));
  } catch (error) {
    console.error("Failed to normalise SignalFix elements payload:", error);
    return [];
  }
};

// Merge extra elements into base signs, skipping any external_id already present.
export const mergeElements = (base = [], extras = []) => {
  const existing = new Set((base || []).map((s) => String(s.external_id)));
  const additions = (extras || []).filter((s) => !existing.has(String(s.external_id)));
  return [...(base || []), ...additions];
};

// Average [lat, lng] of a list of signs (ignoring invalid coords), or null.
export const centroidOf = (items = []) => {
  const valid = (items || []).filter(
    (s) => s && s.lat != null && s.lng != null && !isNaN(s.lat) && !isNaN(s.lng)
  );
  if (!valid.length) return null;
  const lat = valid.reduce((a, s) => a + Number(s.lat), 0) / valid.length;
  const lng = valid.reduce((a, s) => a + Number(s.lng), 0) / valid.length;
  return [lat, lng];
};

// Shift every element (point + polyline geometry) by a lat/lng delta. Used to
// place the location-less demo sample next to the real data so both are visible.
export const translateElements = (elements = [], dLat, dLng) =>
  (elements || []).map((e) => ({
    ...e,
    lat: e.lat + dLat,
    lng: e.lng + dLng,
    geometry: Array.isArray(e.geometry)
      ? e.geometry.map(([la, ln]) => [la + dLat, ln + dLng])
      : e.geometry,
  }));
